import React from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import styled from "styled-components";
import { colors } from "utils";
import { formatDuration } from "./durations";
import { formatShortDate } from "./easternTime";
import { EmptyState } from "./SectionStates";
import { BORDER, Card, CardTitle, WHITE } from "./styles";

const TITLE_ID = "teacher-reports-metrics-chart-title";
const CHART_HEIGHT = 250;
const SECONDS_PER_MINUTE = 60;
const DOT_RADIUS = 3;
const AXIS_CEILING_SECONDS = 10 * SECONDS_PER_MINUTE;

const Sub = styled.p`
  color: ${colors.milkGlass950};
  font-size: 13px;
  margin: 0 0 10px;
`;

const Legend = styled.ul`
  color: ${colors.milkGlass950};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 16px;
  list-style: none;
  margin: 0 0 6px;
  padding: 0;
`;

const LegendItem = styled.li`
  align-items: center;
  display: inline-flex;
  gap: 6px;
`;

const Swatch = styled.i`
  background: ${(p) => p.$color};
  display: inline-block;
  height: 2px;
  width: 14px;
`;

const MEDIAN_FIELD = "median_seconds";
const SERIES = [
  { field: MEDIAN_FIELD, color: "#2563EB" },
  { field: "p90_seconds", label: "p90", color: "#EA580C" },
  { field: "p95_seconds", label: "p95", color: "#9333EA" },
  { field: "p99_seconds", label: "p99", color: "#0891B2" }
];

const WORDINGS = {
  user_wait: {
    titles: { day: "Wait per day", week: "Wait per week" },
    sub: "From session end to report ready. Days and weeks follow US Eastern time. The axis stops at 10 minutes; hover a point for the exact value.",
    typical: "Typical wait",
    empty: "No reports were ready in this period."
  },
  report_generation: {
    titles: { day: "Report generation per day", week: "Report generation per week" },
    sub: "From report request to report ready. Each report counts on the day its session ended, in US Eastern time. The axis stops at 10 minutes; hover a point for the exact value.",
    typical: "Typical generation",
    empty: "No reports were generated in this period."
  }
};

const namedSeries = (wording) =>
  SERIES.map((series) => (series.field === MEDIAN_FIELD ? { ...series, label: wording.typical } : series));

const HoverBox = styled.div`
  background: ${WHITE};
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font-size: 12px;
  padding: 8px 12px;
`;

const HoverValues = styled.dl`
  column-gap: 12px;
  display: grid;
  grid-template-columns: auto auto;
  margin: 4px 0;

  dt {
    font-weight: 400;
  }

  dd {
    font-weight: 600;
    margin: 0;
    text-align: right;
  }
`;

const onAxis = (seconds) => (seconds === null ? null : Math.min(seconds, AXIS_CEILING_SECONDS));

const minuteTicks = (buckets) => {
  const longest = Math.max(0, ...buckets.flatMap((bucket) => SERIES.map((series) => onAxis(bucket[series.field]) || 0)));
  const minutes = Math.max(1, Math.ceil(longest / SECONDS_PER_MINUTE));
  return Array.from({ length: minutes + 1 }, (_, minute) => minute * SECONDS_PER_MINUTE);
};

const formatMinutes = (seconds) => (seconds === 0 ? "0" : `${seconds / SECONDS_PER_MINUTE} min`);

const WaitHoverBox = ({ active, payload, series: namedLines }) => {
  if (!active || !payload?.length) return null;
  const bucket = payload[0].payload;
  return (
    <HoverBox>
      <strong style={{ fontWeight: 700 }}>{formatShortDate(bucket.bucket_start)}</strong>
      <HoverValues>
        {namedLines.map((series) => (
          <React.Fragment key={series.field}>
            <dt>{series.label}</dt>
            <dd>{formatDuration(bucket[series.field])}</dd>
          </React.Fragment>
        ))}
      </HoverValues>
      <div>{bucket.measurement_count} reports</div>
    </HoverBox>
  );
};

const Plot = ({ buckets, wording }) => {
  const ticks = minuteTicks(buckets);
  const namedLines = namedSeries(wording);

  return (
    <>
      <Legend aria-label="Legend">
        {namedLines.map((series) => (
          <LegendItem key={series.field}>
            <Swatch $color={series.color} />
            {series.label}
          </LegendItem>
        ))}
      </Legend>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart data={buckets} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={BORDER} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="bucket_start" tickFormatter={formatShortDate} tick={{ fontSize: 12 }} />
          <YAxis
            domain={[0, ticks[ticks.length - 1]]}
            ticks={ticks}
            tickFormatter={formatMinutes}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<WaitHoverBox series={namedLines} />} isAnimationActive={false} />
          {namedLines.map((series) => (
            <Line
              key={series.field}
              type="linear"
              dataKey={(bucket) => onAxis(bucket[series.field])}
              name={series.label}
              stroke={series.color}
              strokeWidth={2}
              dot={{ r: DOT_RADIUS, fill: series.color }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export const WaitChart = ({ granularity, measurement, buckets, hasMeasurements }) => {
  const wording = WORDINGS[measurement];
  return (
    <Card aria-labelledby={TITLE_ID}>
      <CardTitle id={TITLE_ID}>{wording.titles[granularity]}</CardTitle>
      <Sub>{wording.sub}</Sub>
      {hasMeasurements ? (
        <Plot buckets={buckets} wording={wording} />
      ) : (
        <EmptyState>{wording.empty}</EmptyState>
      )}
    </Card>
  );
};
